'use client';

import { useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import type { BufferGeometry, Object3D } from 'three';
import { type ShaderPrecompilerProps } from '@/app/lcv/components/sections/spartan/shared/webgpu/shader-pre-compiler/types';
import logger from '@/lib/logger';

// How long to wait for the scene to finish building before compiling it
// anyway.
//
// There has to be a ceiling. `onCompiled` is what drops the parked car and
// tunnel out of the draw list (see the canvas's onShaderCompiled), so a
// precompiler that waited forever would leave a full-frame lava pass
// rendering off-screen for the life of the page -- a far worse outcome
// than the warning this guard exists to avoid.
const SETTLE_TIMEOUT_MS = 2000;

// The first drawable object in the scene whose geometry has no `position`
// attribute, or null when every one of them is built.
//
// An R3F <mesh> and its <planeGeometry> child are attached in separate
// steps, so for a moment the mesh is in the graph carrying the default
// empty BufferGeometry. Normally that gap closes inside one commit and
// nothing observes it; on a slow machine it can span a frame, and
// anything that walks the scene in that window sees a mesh with no
// vertices.
const findUnbuiltGeometry = (scene: Object3D): Object3D | null => {
  let unbuilt: Object3D | null = null;
  scene.traverse(object => {
    if (unbuilt) return;
    const drawable = object as Object3D & {
      isMesh?: boolean;
      isPoints?: boolean;
      isLine?: boolean;
      geometry?: BufferGeometry;
    };
    if (!drawable.isMesh && !drawable.isPoints && !drawable.isLine) return;
    if (!drawable.geometry?.attributes?.position) unbuilt = object;
  });
  return unbuilt;
};
export const ShaderPrecompiler = ({
  readyToCompile,
  onCompiled,
  onError
}: ShaderPrecompilerProps) => {
  const gl = useThree(state => state.gl);
  const scene = useThree(state => state.scene);
  const camera = useThree(state => state.camera);
  const hasCompiledRef = useRef(false);
  useEffect(() => {
    if (!readyToCompile || hasCompiledRef.current) {
      return;
    }
    let frame = 0;
    const startedAtMs = performance.now();
    const compile = () => {
      hasCompiledRef.current = true;
      gl.compileAsync(scene, camera).then(onCompiled, onError);
    };

    // WAIT FOR THE SCENE TO BE FULLY BUILT BEFORE COMPILING IT.
    const waitForScene = () => {
      const unbuilt = findUnbuiltGeometry(scene);
      if (!unbuilt) {
        compile();
        return;
      }
      if (performance.now() - startedAtMs >= SETTLE_TIMEOUT_MS) {
        // Compile regardless -- see SETTLE_TIMEOUT_MS. Named so the
        // object that never finished building is identifiable rather
        // than just a warning about "geometry".
        logger.warn(`[shader-precompile] "${unbuilt.name || unbuilt.type}" still has no ` + `position attribute after ${SETTLE_TIMEOUT_MS}ms; compiling anyway`);
        compile();
        return;
      }
      frame = requestAnimationFrame(waitForScene);
    };
    frame = requestAnimationFrame(waitForScene);
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyToCompile]);
  return null;
};